import { z } from "zod";
import { businessDate, money, note, objectId } from "./common.js";
/**
 * Reports (§34), Daily Cash Tally (§20) and Daily Profit (§21).
 *
 * THE RULE THAT SHAPES THIS WHOLE FILE — §21:
 *
 *   CASH FLOW is not PROFIT.
 *
 * They are computed from different account classes by different functions and are never
 * added together:
 *
 *   Cash flow  — movement in ASSET accounts of kind BANK and CASH. Answers "what is in
 *                the drawer and the bank".
 *   Profit     — INCOME accounts less EXPENSE accounts. Answers "did the business earn".
 *
 * A business can take ₹10,00,000 through the door in a day and make a loss; it can bank
 * nothing and be profitable. Conflating the two is the single most common way a small
 * business ends up insolvent while believing it is doing well.
 */
export const reportRangeSchema = z
    .object({
    from: businessDate,
    to: businessDate,
})
    .refine((v) => v.from <= v.to, { message: "The start date must not be after the end date", path: ["from"] });
export const asOfSchema = z.object({
    asOf: businessDate.optional(),
});
/* -------------------------------------------------------------------------- */
/* Daily Cash Tally (§20)                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Counting the drawer at end of day.
 *
 * The field names deliberately mirror the AMIRI workbook — Cash Received, Cash Paid,
 * Cash Closing, Party Given, Party Taken, Tally Difference — so the person who has been
 * keeping the spreadsheet recognises the screen.
 *
 * §62 governs the outcome: if expected is ₹10,00,000 and counted is ₹9,80,000, the tally
 * records SHORT ₹20,000 and stops. It never adjusts the expectation to match the count.
 */
export const recordTallySchema = z.object({
    date: businessDate,
    cashAccountId: objectId,
    /** What was physically counted in the drawer. */
    actualClosing: money.refine((v) => v >= 0, "A counted amount cannot be negative"),
    notes: note(1000),
});
export const TALLY_STATUS_LABEL = {
    PENDING: "Not counted",
    MATCHED: "Matched",
    SHORT: "Short",
    EXCESS: "Excess",
};
//# sourceMappingURL=reports.js.map